// Real-time sync with lanewerk-server (protocol: crates/lanewerk-server/src/protocol.rs).
import type { Store } from "./store";

const HELLO = 0x01, SYNC = 0x02, UPDATE = 0x03, PRESENCE = 0x04, ROLE = 0x05, GONE = 0x06;

export type SyncStatus = "connecting" | "online" | "offline";
/** First frame on every socket: the viewer's role on this board and the account. */
export interface RoleInfo { role: "read" | "write" | "admin"; auth: boolean; user: { id: string; name: string; short: string } | null }

function frame(tag: number, payload: Uint8Array) {
  const out = new Uint8Array(payload.length + 1);
  out[0] = tag;
  out.set(payload, 1);
  return out;
}

export class SyncClient {
  status: SyncStatus = "connecting";
  private ws: WebSocket | null = null;
  private retry = 500;
  private synced = false;
  private early: Uint8Array[] = [];
  private received = false;
  private stopped = false;
  /** Number of completed (re)connections – the e2e test watches it. */
  connects = 0;
  /** Presence frames from the others in the room. */
  onPresence: (payload: Uint8Array) => void = () => {};
  /** Called after every completed (re)connection. */
  onSynced: () => void = () => {};
  /** The role frame of each connection. */
  onRole: (r: RoleInfo) => void = () => {};
  /** The board was moved to the trash (GONE frame); the server closes the socket. */
  onGone: () => void = () => {};
  /** The socket closed before the server said anything: refused (no session or role) or unreachable. */
  onRefused: () => void = () => {};

  constructor(private url: string, private store: Store, private onStatus: (s: SyncStatus) => void) {
    store.onLocalChange = () => this.flush();
  }

  start() {
    if (this.stopped) return;
    this.setStatus("connecting");
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.synced = false;
    this.early = [];
    this.received = false;
    ws.onopen = () => ws.send(frame(HELLO, this.store.engine.version()));
    ws.onmessage = (ev) => this.onMessage(new Uint8Array(ev.data as ArrayBuffer));
    ws.onclose = () => {
      this.ws = null;
      if (this.stopped) return;
      if (!this.received) this.onRefused();
      this.setStatus("offline");
      setTimeout(() => this.start(), this.retry);
      this.retry = Math.min(this.retry * 2, 10_000);
    };
  }

  /** Gives up for good (no access to the board). */
  stop() {
    this.stopped = true;
    this.ws?.close();
  }

  private onMessage(m: Uint8Array) {
    const tag = m[0], payload = m.subarray(1);
    this.received = true;
    if (tag === ROLE) {
      try { this.onRole(JSON.parse(new TextDecoder().decode(payload)) as RoleInfo); } catch { /* ignore a malformed frame */ }
    } else if (tag === GONE) {
      this.stop();
      this.onGone();
    } else if (tag === PRESENCE) {
      this.onPresence(payload);
    } else if (tag === UPDATE) {
      // room updates can overtake the SYNC reply: keep them until SYNC is applied
      if (!this.synced) this.early.push(payload.slice());
      else this.store.importRemote(payload);
    } else if (tag === SYNC) {
      const n = new DataView(payload.buffer, payload.byteOffset).getUint32(0, true);
      const serverVv = payload.slice(4, 4 + n);
      const updates = payload.subarray(4 + n);
      if (updates.length) this.store.importRemote(updates);
      for (const u of this.early) this.store.importRemote(u);
      this.early = [];
      // everything the server is missing (offline edits) in one message; the queue is covered by it
      const missing = this.store.engine.exportUpdatesSince(serverVv);
      while (this.store.engine.takeOutgoing()) { /* drop */ }
      this.ws?.send(frame(UPDATE, missing));
      this.synced = true;
      this.retry = 500;
      this.connects++;
      this.setStatus("online");
      this.onSynced();
    }
  }

  /** Sends queued local updates; while offline they stay in the CRDT history and go out on reconnect. */
  flush() {
    if (!this.synced || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    for (let u = this.store.engine.takeOutgoing(); u; u = this.store.engine.takeOutgoing()) this.ws.send(frame(UPDATE, u));
  }

  /** Sends a presence payload; dropped while offline (presence is never queued). */
  sendPresence(payload: Uint8Array) {
    if (this.synced && this.ws?.readyState === WebSocket.OPEN) this.ws.send(frame(PRESENCE, payload));
  }

  private setStatus(s: SyncStatus) {
    this.status = s;
    this.onStatus(s);
  }
}
